<template>
    <div
        v-editable="blok"
        class="sun-widget"
    >
        <div class="sun-widget__left">
            <div
                class="sun-widget__date-wrapper"
                @click="bringUpCalendar"
            >
                <input
                    id="date-picker"
                    type="date"
                    name="date-picker"
                    :min="today"
                    :max="yearFromNow"
                    class="sun-widget__date"
                    :value="date"
                    @input="input"
                >

                <div class="sun-widget__date-text">
                    <SunDate format="Digit" />
                </div>

                <img
                    class="sun-widget__edit-image"
                    src="/images/sun/pencil.svg"
                    alt="Image of a pencil for editing functionality"
                >
            </div>

            <div class="sun-widget__text-wrapper">
                <h2 class="sun-widget__title">
                    Sunrise & Sunset
                </h2>
                <h3 class="sun-widget__subtitle">
                    Times for
                    <SunDate format="Text" />
                </h3>
            </div>
            <div class="sun-widget__time-wrapper">
                <div class="sun-widget__sunrise sun-widget-item">
                    <h3 class="sun-widget-item__title">
                        Sunrise
                    </h3>
                    <h2 class="sun-widget-item__time">
                        {{ sunriseFormatted }}
                    </h2>
                </div>
                <div class="sun-widget__sunset sun-widget-item">
                    <h3 class="sun-widget-item__title">
                        Sunset
                    </h3>
                    <h2 class="sun-widget-item__time">
                        {{ sunsetFormatted }}
                    </h2>
                </div>
            </div>
        </div>
        <div class="sun-widget__right">
            <NuxtImg
                v-if="blok.image?.filename"
                class="sun-widget__image"
                :src="blok.image.filename"
                :alt="blok.image.alt"
                width="634"
                height="562"
                fit="cover"
                format="auto"
                loading="lazy"
                sizes="xs:100vw sm:100vw md:320px"
            />
        </div>
    </div>
</template>

<script setup>
import { storeToRefs } from 'pinia'
import useGlobalStore from '~/stores/GlobalStore'

import SunDate from './sunstate-date.vue'
import './sunstate.scss'

const props = defineProps({
    blok: {
        type: Object,
        required: true,
    },
    month: {
        type: Number,
        required: true,
    },
})

const globalStore = useGlobalStore()
const { userDate, celsiusUnit } = storeToRefs(globalStore)

const date = ref('')

const sunrise = ref('06:00:00')
const sunset = ref('18:00:00')

const sunriseFormatted = computed(() => {
    return new Date(`${date.value} ${sunrise.value}`).toLocaleTimeString(celsiusUnit.value ? 'en-GB' : 'en-US', {
        hour: '2-digit',
        minute: '2-digit',
    })
})

const sunsetFormatted = computed(() => {
    return new Date(`${date.value} ${sunset.value}`).toLocaleTimeString(celsiusUnit.value ? 'en-GB' : 'en-US', {
        hour: '2-digit',
        minute: '2-digit',
    })
})

date.value = userDate.value.toISOString().slice(0, 10)

const today = new Date().toISOString().slice(0, 10)
const yearFromNow = new Date(
    new Date().setDate(new Date().getDate() + 365),
).toISOString().slice(0, 10)

function bringUpCalendar() {
    document.querySelector('#date-picker').showPicker()
}

function input(event) {
    date.value = event.target.value
    userDate.value = new Date(event.target.value)

    fetchSunriseSunsetData()
}

function calculateDefaultDate() {
    const currentDate = new Date()
    const currentMonth = currentDate.getMonth()
    const currentYear = currentDate.getFullYear()

    if (props.month === currentMonth || !props.month) {
        userDate.value = new Date()
        date.value = new Date().toISOString().slice(0, 10)
    } else {
        const providedMonthString = String(props.month + 1).padStart(2, '0')
        const shownYear = props.month > currentMonth ? currentYear : currentYear + 1

        userDate.value = new Date(
            `${shownYear}-${providedMonthString}-01`,
        )
        date.value = `${shownYear}-${providedMonthString}-01`
    }

    fetchSunriseSunsetData()
}

async function fetchSunriseSunsetData() {
    const { data } = await useAsyncData('sunrise-sunset', () => $fetch(`/api/sunrise-sunset/by-day?date=${date.value}`))

    sunrise.value = data.value.sunrise
    sunset.value = data.value.sunset
}

calculateDefaultDate()
</script>
